import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { PostEntity } from '@interpals/api-web';

import { DefaultReduxErrorPayload } from 'types/errors';
import { ReduxRequestState } from 'types/redux';
import { createLike, deleteLike } from 'redux/likes/actions';
import { updatePostCommentsCount } from 'redux/comments/actions';
import { logout } from 'redux/auth/actions';

import {
    createPost,
    deletePost,
    editPost,
    fetchPinnedPosts,
    getPost,
    getPosts,
    moderationDeletePost,
    pinPost,
    unpinPost,
} from './actions';

export type FeedScreen = 'home' | 'user' | 'group';

export interface PostsState {
    singlePost: PostEntity | null;
    homeFeed: PostEntity[];
    userFeed: PostEntity[];
    groupFeed: PostEntity[];
    homeFeedFilters?: FeedFilters;
    hasMore: boolean;
    pinnedPosts: ReduxRequestState<PostEntity[], DefaultReduxErrorPayload>;
}

export interface FeedFilters {
    languages?: string[];
    countries?: string[];
    following?: boolean;
}

const initialState: PostsState = {
    singlePost: null,
    homeFeed: [],
    userFeed: [],
    groupFeed: [],
    hasMore: true,
    pinnedPosts: {
        data: [],
        error: null,
        loading: false,
    },
};

export const getPostsArrayByScreen = (state: PostsState, screen: FeedScreen) => {
    switch (screen) {
        case 'home':
            return state.homeFeed;
        case 'group':
            return state.groupFeed;
        case 'user':
            return state.userFeed;
    }
};

const postsSlice = createSlice({
    name: 'posts',
    initialState,
    reducers: {
        clearPosts: (state) => {
            state.userFeed = initialState.userFeed;
            state.groupFeed = initialState.groupFeed;
            state.pinnedPosts = initialState.pinnedPosts;
            state.hasMore = true;
            state.singlePost = null;
        },
        setHomeFeedFilters: (state, { payload }) => {
            state.homeFeedFilters = payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createPost.fulfilled, (state, { payload: post, meta }) => {
            getPostsArrayByScreen(state, meta.arg.screen).unshift(post);
        });

        builder.addCase(getPost.fulfilled, (state, { payload }) => {
            state.singlePost = payload;
        });

        builder.addCase(getPosts.fulfilled, (state, { payload, meta }) => {
            const { data, hasMore } = payload;

            switch (meta.arg.screen) {
                case 'home':
                    state.homeFeed = meta.arg.reset ? [...data] : [...state.homeFeed, ...data];
                    break;
                case 'user':
                    state.userFeed = meta.arg.reset ? [...data] : [...state.userFeed, ...data];
                    break;
                case 'group':
                    state.groupFeed = meta.arg.reset ? [...data] : [...state.groupFeed, ...data];
                    break;
            }

            state.hasMore = hasMore;
        });

        builder.addCase(editPost.fulfilled, (state, { payload: updated }) => {
            const postId = updated.id;

            state.homeFeed = state.homeFeed.map((post) => (post.id === postId ? updated : post));
            state.userFeed = state.userFeed.map((post) => (post.id === postId ? updated : post));
            state.groupFeed = state.groupFeed.map((post) => (post.id === postId ? updated : post));

            state.singlePost = state.singlePost?.id === postId ? updated : state.singlePost;
        });

        builder.addCase(fetchPinnedPosts.fulfilled, (state, { payload }) => {
            state.pinnedPosts.data = payload;
        });

        builder.addCase(pinPost.fulfilled, (state, { payload }) => {
            state.pinnedPosts.loading = false;
            state.pinnedPosts.data.unshift(payload);
            const index = state.groupFeed.findIndex((post) => post.id === payload.id);
            state.groupFeed[index] = { ...state.groupFeed[index], pinned: true };
        });

        builder.addCase(pinPost.rejected, (state, { payload }) => {
            state.pinnedPosts.loading = false;
            if (payload?.reason) {
                state.pinnedPosts.error = payload;
            }
        });

        builder.addCase(pinPost.pending, (state) => {
            state.pinnedPosts.loading = true;
        });

        builder.addCase(unpinPost.fulfilled, (state, { payload: post }) => {
            // Remove from pinned posts
            state.pinnedPosts.data = state.pinnedPosts.data.filter((p) => p.id !== post.id);

            // Find in feed (if exists)
            const feedIndex = state.groupFeed.findIndex((p) => p.id === post.id);

            if (feedIndex !== -1) {
                // Found in feed, unpin
                state.groupFeed[feedIndex] = { ...state.groupFeed[feedIndex], pinned: false };
            } else {
                // Insert into feed in correct position by post id
                // (Posts are sorted by time/postId descending)

                // Find index of first post with id less than post.id
                const insertIndex = state.groupFeed.findIndex((p) => p.id < post.id);

                // Insert post at index
                state.groupFeed.splice(insertIndex, 0, post);
            }
        });

        builder.addCase(createLike.fulfilled, (state, { meta }) => {
            if (meta.arg.entity_type !== 'post') return;

            const updateLikes = (postsArray: PostEntity[]) => {
                const post = postsArray.find((p) => p.id === meta.arg.entity_id);
                const likesComments = post?.likes_comments;

                if (likesComments) {
                    likesComments.user_liked = true;
                    likesComments.likes += 1;
                }
            };

            [state.homeFeed, state.userFeed, state.groupFeed, state.pinnedPosts.data].forEach(updateLikes);
        });

        builder.addCase(deleteLike.fulfilled, (state, { meta }) => {
            if (meta.arg.entity_type !== 'post') return;

            const updateLikes = (postsArray: PostEntity[]) => {
                const post = postsArray.find((p) => p.id === meta.arg.entity_id);
                const likesComments = post?.likes_comments;

                if (likesComments) {
                    likesComments.user_liked = false;
                    likesComments.likes -= 1;
                }
            };

            [state.homeFeed, state.userFeed, state.groupFeed, state.pinnedPosts.data].forEach(updateLikes);
        });

        builder.addCase(updatePostCommentsCount, (state, { payload }) => {
            const updateComments = (postsArray: PostEntity[]) => {
                const post = postsArray.find((item) => item.id === payload.entityId) || state.singlePost;
                const likesComments = post && post.likes_comments;

                if (likesComments && payload.actionType === 'add') {
                    likesComments.comments = likesComments.comments ? likesComments.comments + 1 : 1;
                } else if (likesComments && payload.actionType === 'delete') {
                    likesComments.comments = likesComments.comments ? Math.max(likesComments.comments - 1, 0) : 0;
                }
            };

            [state.homeFeed, state.userFeed, state.groupFeed, state.pinnedPosts.data].forEach(updateComments);
        });

        builder.addCase(logout.fulfilled, () => {
            return initialState;
        });

        builder.addMatcher(isAnyOf(deletePost.fulfilled, moderationDeletePost), (state, { payload: postId }) => {
            const removePost = (feed: PostEntity[]) => {
                return feed.filter((post) => post.id !== postId);
            };

            [state.homeFeed, state.userFeed, state.groupFeed, state.pinnedPosts.data].forEach(removePost);
        });
    },
});

export default postsSlice.reducer;
export const { setHomeFeedFilters } = postsSlice.actions;
export const { clearPosts } = postsSlice.actions;
